import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DailyExpenseDay } from "../../types/daily-expense.type";
import { formatNumber } from "../../utils";
import ExpenseHistory from "./ExpenseHistory";
import SalesExpenseTable from "./SalesExpenseTable";
import {
  CategoryLayout,
  GROUP_LABEL,
  OtherRow,
  buildFormState,
  computeFormTotals,
  formatAmount,
  parseAmount,
} from "./dailyExpenseForm";

/** ฝ่ายบริหาร beside the wider อื่นๆ list. */
const LIST_COLUMNS = "minmax(0, 1fr) minmax(0, 1.4fr)";

function ListLine({ label, amount }: Readonly<{ label: string; amount: string }>) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      gap={1.5}
      sx={{ py: 1, borderBottom: 1, borderColor: "divider" }}
    >
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        {amount}
      </Typography>
    </Stack>
  );
}

interface ReviewDayContentProps {
  day: DailyExpenseDay;
  layout: CategoryLayout;
}

/**
 * The S2 document as head office reads it in the S3 drawer: the saved lines only, in the same
 * ฝ่ายขาย table the branch keys into (read-only), then ฝ่ายบริหาร, อื่นๆ, the totals and the history.
 */
export default function ReviewDayContent({ day, layout }: Readonly<ReviewDayContentProps>) {
  const form = buildFormState(day, layout, false);
  const totals = computeFormTotals(form, layout);
  const isNoExpense = Boolean(day.document?.no_expense);

  /** "ค่าปะยางรถ · ฝ่ายขาย · KB02" — the route only where a ฝ่ายขาย line names one. */
  const otherLabelOf = (row: OtherRow) => {
    const route = day.routes.find((candidate) => candidate.route_uuid === row.routeUuid);
    return [row.description, row.group ? GROUP_LABEL[row.group] : "", route?.route_code ?? ""]
      .filter(Boolean)
      .join(" · ");
  };

  return (
    <Stack gap={2.25} sx={{ fontVariantNumeric: "tabular-nums" }}>
      {isNoExpense ? (
        <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: "action.hover", textAlign: "center" }}>
          <Typography fontWeight={600}>สาขาแจ้งว่าไม่มีค่าใช้จ่ายวันนี้</Typography>
          <Typography variant="body2" color="text.secondary">
            ยอดรวม {formatNumber(0)} บาท
          </Typography>
        </Box>
      ) : (
        <>
          <SalesExpenseTable
            routes={day.routes}
            columns={layout.salesColumns}
            sales={form.sales}
            totals={totals}
            readOnly
          />
          <Box sx={{ display: "grid", gridTemplateColumns: LIST_COLUMNS, gap: 2.5 }}>
            <Stack>
              <Typography fontWeight={600}>ฝ่ายบริหาร</Typography>
              {layout.adminFields.map((category) => (
                <ListLine
                  key={category.code}
                  label={category.name}
                  amount={formatAmount(parseAmount(form.admin[category.code] ?? ""))}
                />
              ))}
            </Stack>
            <Stack>
              <Typography fontWeight={600}>อื่นๆ</Typography>
              {form.others.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  ไม่มีรายการ
                </Typography>
              )}
              {form.others.map((row) => (
                <ListLine
                  key={row.id}
                  label={otherLabelOf(row)}
                  amount={formatAmount(parseAmount(row.amountText))}
                />
              ))}
            </Stack>
          </Box>
        </>
      )}

      <Stack
        direction="row"
        gap={3}
        flexWrap="wrap"
        sx={{ px: 1.75, py: 1.5, borderRadius: 2, bgcolor: "background.default", color: "text.secondary" }}
      >
        <Typography variant="body2">
          ฝ่ายขาย{" "}
          <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
            {formatNumber(day.totals.sales_total)}
          </Box>
        </Typography>
        <Typography variant="body2">
          ฝ่ายบริหาร{" "}
          <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
            {formatNumber(day.totals.admin_total)}
          </Box>
        </Typography>
        <Typography variant="body2">
          รวมทั้งหมด{" "}
          <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
            {formatNumber(day.totals.total)}
          </Box>{" "}
          บาท
        </Typography>
      </Stack>

      <ExpenseHistory history={day.history} />
    </Stack>
  );
}
